import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useRef, useState } from 'react';
import { createContainer } from 'unstated-next';
import * as enabledApi from '../../api/enabledPlayApi';
import {
    ENABLED_ACCESS_EXPIRATION,
    ENABLED_ACCESS_TOKEN_STORE,
    ENABLED_REFRESH_TOKEN_STORE,
    getEnabledClientId,
    getEnabledClientSecret,
} from '../../constants/Keys';
import { getAuthRedirectUri } from '../../constants/Urls';
import TokenResponse from '../../models/authentication/TokenResponse';
import IResult from '../../models/result/IResult';
import useStateRef from '../useStateRef';

type AuthListener = () => void;

function useAuthContainer() {
    const [isAuthenticated, setIsAuthenticated, isAuthenticatedRef] = useStateRef<boolean>(false);
    const [accessToken, setAccessToken, accessTokenRef] = useStateRef<string | null>(null);
    const [refreshToken, setRefreshToken, refreshTokenRef] = useStateRef<string | null>(null);
    const [expirationDate, setExpirationDate, expirationDateRef] = useStateRef<Date>(new Date());
    const [isLoading, setIsLoading, isLoadingRef] = useStateRef<boolean>(true);
    const [isLoadingDataComplete, setIsLoadingDataComplete, isLoadingDataCompleteRef] = useStateRef<boolean>(false);
    const [isInitialized, setIsInitialized, isInitializedRef] = useStateRef<boolean>(false);
    const signoutListenersRef = useRef<AuthListener[]>([]);
    const [errors, setErrors] = useState<string[]>([]);

    const notifyListeners = (listeners: AuthListener[]) => {
        listeners.forEach((listener) => {
            try {
                listener();
            } catch (error) {
                console.error('Error in signout listener:', error);
            }
        });
    };

    const addSignoutListener = (listener: () => void) => {
        signoutListenersRef.current.push(listener);
    };

    const removeSignoutListener = (listener: () => void) => {
        signoutListenersRef.current = signoutListenersRef.current.filter((l) => l !== listener);
    };

    const initializeFromStorage = () => {
        setIsLoading(true);
        try {
            const accessToken = sessionStorage.getItem(ENABLED_ACCESS_TOKEN_STORE);
            const refreshToken = localStorage.getItem(ENABLED_REFRESH_TOKEN_STORE);
            const expirationDate = localStorage.getItem(ENABLED_ACCESS_EXPIRATION);
            if (refreshToken) {
                setAccessToken(accessToken);
                setRefreshToken(refreshToken);
                if (expirationDate) setExpirationDate(new Date(expirationDate));
                setIsAuthenticated(true);
                if (!isInitializedRef.current) {
                    setIsInitialized(true);
                }
                console.log('Auth.initialzeFromStorage: Authenticated');
            } else {
                console.log('Auth.initialzeFromStorage: No refresh token found');
            }

            return true;
        } catch (e) {
            console.log('Auth.initialzeFromStorage:', e);
            return false;
        } finally {
            setIsLoading(false);
        }
    };
    const loginFromCode = async (code: string): Promise<IResult<TokenResponse>> => {
        try {
            setIsLoading(true);
            const result = await enabledApi.getToken(getEnabledClientId(), code, getAuthRedirectUri());
            if (result?.resultType == 'Ok' && result.data) {
                sessionStorage.setItem(ENABLED_ACCESS_TOKEN_STORE, result.data.access_token);
                localStorage.setItem(ENABLED_REFRESH_TOKEN_STORE, result.data.refresh_token);
                localStorage.setItem(ENABLED_ACCESS_EXPIRATION, result.data.access_token_expiration);
                setAccessToken(result.data.access_token);
                setRefreshToken(result.data.refresh_token);
                setExpirationDate(new Date(result.data.access_token_expiration));
                setIsAuthenticated(true);
                if (!isInitializedRef.current) {
                    setIsInitialized(true);
                }
            } else if (result.errors != null && result.errors.length > 0) {
                setErrors([...result.errors]);
            }
            return result;
        } catch (error) {
            setErrors([error]);

            return new UnexpectedResult<TokenResponse>();
        } finally {
            setIsLoading(false);
        }
    };
    const getNewToken = async (): Promise<IResult<TokenResponse>> => {
        setIsLoading(true);
        try {
            const latestRefresh = localStorage.getItem(ENABLED_REFRESH_TOKEN_STORE);
            if (!latestRefresh) {
                setIsLoading(false);
                return { resultType: 'Invalid', errors: ['No refresh token found'] };
            }
            const result = await enabledApi.refreshToken(getEnabledClientId(), getEnabledClientSecret(), latestRefresh);
            if (result.resultType == 'Offline' || result.resultType == 'NoResponse') {
                // just use the token from storage and early return
                const storage = initializeFromStorage();
                if (storage) {
                    return { resultType: 'Ok' };
                }
            }
            if (result?.resultType == 'Ok' && result.data) {
                // set the new tokens in session and local storage
                sessionStorage.setItem(ENABLED_ACCESS_TOKEN_STORE, result.data.access_token);
                localStorage.setItem(ENABLED_REFRESH_TOKEN_STORE, result.data.refresh_token);
                localStorage.setItem(ENABLED_ACCESS_EXPIRATION, result.data.access_token_expiration);
                setAccessToken(result.data.access_token);
                setRefreshToken(result.data.refresh_token);
                setExpirationDate(new Date(result.data.access_token_expiration));
                setIsAuthenticated(true);
                if (!isInitializedRef.current) {
                    setIsInitialized(true);
                }
            }
            if (result.errors != null && result.errors.length > 0 && result.resultType != 'Ok') {
                setErrors([...result.errors]);
                logout();
            }
            return result;
        } catch (error) {
            setErrors([error]);
            return { resultType: 'Invalid', errors: [error] };
        } finally {
            setIsLoading(false);
        }
    };

    const handleIsLoading = (loading: boolean) => {
        setIsLoading(loading);
    };

    const logout = () => {
        setIsLoading(true);
        notifyListeners(signoutListenersRef.current);
        sessionStorage.clear();
        localStorage.clear();
        setIsAuthenticated(false);
        setAccessToken(null);
        setRefreshToken(null);
        setExpirationDate(new Date());
        signoutListenersRef.current = [];
        setIsLoading(false);
    };

    return {
        isInitialized,
        setIsInitialized,
        isInitializedRef,
        isAuthenticated,
        isAuthenticatedRef,
        accessToken,
        accessTokenRef,
        refreshToken,
        refreshTokenRef,
        expirationDate,
        expirationDateRef,
        isLoading,
        isLoadingRef,
        isLoadingDataComplete,
        isLoadingDataCompleteRef,
        setIsLoadingDataComplete,
        handleIsLoading,
        errors,
        logout,
        loginFromCode,
        getNewToken,
        initializeFromStorage,
        addSignoutListener,
        removeSignoutListener,
    };
}

const AuthContainer = createContainer(useAuthContainer);
export default AuthContainer;
